/*
 * Inspired by "Chord Image Generator" .NET source code of
 * Copyright (C)  2009-2012 Einar Egilsson [einar@einaregilsson.com]
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

export class GuitarChordDraw {
    private MUTED = -1;
    private FRET_COUNT = 5;
    private STRING_COUNT = 6;
    private foreBrush = '#222222';
    private backBrush = 'transparent';
    private fillColor = 'rgba(5,97,103,0.8)';
    private relativeSize = 2;
    private positions: number[] = [];
    private readonly fretWidth: number;
    private readonly boxWidth: number;
    private readonly boxHeight: number;
    private readonly yStart: number;
    private readonly xStart: number;
    private readonly width: number;
    private readonly height: number;
    private baseFret: number = 0;
    private canvasContext: CanvasRenderingContext2D | null = null;


    constructor(canvas: HTMLCanvasElement, chord: string) {
        this.parseChord(chord);
        this.fretWidth = 4.5 * this.relativeSize;
        this.boxWidth = this.boxHeight = this.FRET_COUNT * this.fretWidth;
        this.yStart = this.xStart = this.fretWidth;
        this.width = (this.boxWidth + 4 * this.fretWidth);
        this.height = this.boxHeight + this.fretWidth * 1.3;
        this.draw(canvas);
    }


    private parseChord(chord: string) {
        const parts = chord.toUpperCase().split('-');
        let maxFret = 0;
        let minFret = Number.MAX_VALUE;
        for (let i = 0; i < parts.length; i++) {
            if (parts[i] === 'X') {
                this.positions[i] = this.MUTED;
            } else {
                this.positions[i] = parseInt(parts[i], 10);
                maxFret = Math.max(maxFret, this.positions[i]);
                if (this.positions[i] !== 0) {
                    minFret = Math.min(minFret, this.positions[i]);
                }
            }
        }
        this.baseFret = minFret;
        if (maxFret <= 5) {
            this.baseFret = 1;
        }
    }

    private draw(canvas: HTMLCanvasElement) {
        canvas.width = this.width;
        canvas.height = this.height;
        this.canvasContext = canvas.getContext('2d');
        this.drawBackground();
        this.drawBox();
        this.drawChordPositions();
        this.drawFretNumber();
    }

    private drawBackground() {
        this.canvasContext!.fillStyle = this.backBrush;
        this.canvasContext!.fillRect(0, 0, this.width, this.height);
    }

    private drawBox() {
        let i;
        let x;
        let y;
        this.canvasContext!.lineWidth = 0.9;
        this.canvasContext!.strokeStyle = this.foreBrush;
        const totalFretWidth = this.fretWidth;
        for (i = 0; i <= this.FRET_COUNT; i++) {
            y = this.yStart + i * totalFretWidth;
            this.canvasContext!.beginPath();
            this.canvasContext!.moveTo(this.xStart, y);
            this.canvasContext!.lineTo(this.xStart + this.boxWidth, y);
            this.canvasContext!.stroke();
        }
        for (i = 0; i < this.STRING_COUNT; i++) {
            x = this.xStart + (i * totalFretWidth);
            this.canvasContext!.beginPath();
            this.canvasContext!.moveTo(x, this.yStart);
            this.canvasContext!.lineTo(x, this.yStart + this.boxHeight);
            this.canvasContext!.stroke();
        }
        if (this.baseFret === 1) {
            this.canvasContext!.fillRect(this.xStart, this.yStart, this.boxWidth, 0);
        }
    }

    private drawFinger(xPos: number, yPos: number) {
        const halfDot = this.fretWidth / 2;
        this.canvasContext!.beginPath();
        this.canvasContext!.arc(xPos + halfDot, yPos + halfDot, halfDot, 0, 2 * Math.PI);
        this.canvasContext!.fill();
    }

    private drawMuted(xPos: number) {
        const yPos = this.yStart - this.fretWidth;
        const markerWidth = 0.7 * this.fretWidth;
        const markerXPos = xPos + ((this.fretWidth - markerWidth) / 2);
        this.canvasContext!.beginPath();
        this.canvasContext!.moveTo(markerXPos, yPos);
        this.canvasContext!.lineTo(markerXPos + markerWidth, yPos + markerWidth);
        this.canvasContext!.stroke();
        this.canvasContext!.beginPath();
        this.canvasContext!.moveTo(markerXPos, yPos + markerWidth);
        this.canvasContext!.lineTo(markerXPos + markerWidth, yPos);
        this.canvasContext!.stroke();
    }

    private drawFretNumber() {
        if (this.baseFret <= 1) {
            return;
        }
        this.canvasContext!.font = 'normal ' + (this.relativeSize * 4) + 'pt Raleway';
        const x = this.xStart + this.boxWidth + this.fretWidth / 1.4;
        const y = this.yStart + this.fretWidth;
        this.canvasContext!.fillText(this.baseFret + 'fr', x, y);
    }

    private drawChordPositions() {
        const yOffset = this.yStart - this.fretWidth;
        let yPos;
        let xPos;
        const halfDot = this.fretWidth / 2;
        this.canvasContext!.fillStyle = this.fillColor;
        for (let i = 0; i < this.positions.length; i++) {
            const absolutePos = this.positions[i];
            const relativePos = absolutePos - this.baseFret + 1;
            xPos = this.xStart - halfDot + i * this.fretWidth;
            yPos = relativePos * this.fretWidth + yOffset;
            if (relativePos > 0) {
                this.drawFinger(xPos, yPos);
            } else if (absolutePos === this.MUTED) {
                this.drawMuted(xPos);
            }
        }
    }
}


